/**
 * Staff Action Centre — Risk Assessments tab.
 *
 * List of risk assessment programs assigned to the current user as maker
 * or checker. Clicking a row opens the shared RiskAssessmentExecutionModal
 * in the appropriate mode (maker write / checker approve).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import Skeleton from '../../../components/common/Skeleton';
import {
    riskAssessmentProgramService,
    RiskAssessmentProgram,
} from '../../../services/riskAssessmentProgramService';
import FormControl from '../../../components/common/FormControl';
import Pagination from '../../../components/common/Pagination';
import NoDataFound from '../../../components/common/NoDataFound';
import { statusBadgeCss } from '../../../utils/statusVariant';
import { useAuth } from '../../../contexts/AuthContext';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';
import RiskAssessmentExecutionModal from '../../../components/risk/RiskAssessmentExecutionModal';
import { visibleForScope, isClosedProgram } from './workArchive';

interface WorkRiskAssessmentsBodyProps {
    scope?: 'active' | 'archive';
    readOnly?: boolean;
}

const WorkRiskAssessmentsBody: React.FC<WorkRiskAssessmentsBodyProps> = ({
    scope = 'active',
    readOnly = false,
}) => {
    const { state } = useAuth();
    const { formatDate } = useEntitySettings();
    const myId = state.user?.id;
    const [rows, setRows] = useState<RiskAssessmentProgram[]>([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState<number | null>(null);
    const [openIsChecker, setOpenIsChecker] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    // Deep-link from calendar drill-down: /work?tab=risk-assessments&open=<id>
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        const o = searchParams.get('open');
        if (!o) return;
        const id = parseInt(o, 10);
        if (Number.isFinite(id)) setOpenId(id);
        const next = new URLSearchParams(searchParams);
        next.delete('open');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const [makerRows, checkerRows] = await Promise.all([
                riskAssessmentProgramService.list({ assigned_to_me: true }),
                riskAssessmentProgramService.list({ checker_for_me: true }),
            ]);
            const map = new Map<number, RiskAssessmentProgram>();
            for (const r of [...makerRows, ...checkerRows]) map.set(r.id, r);
            setRows(Array.from(map.values()));
        } catch (e: any) {
            // Access is already enforced on the view page; swallow load errors
            // (e.g. "no access to RSK_RAT_PROGRAMS") instead of surfacing a toast.
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => { setPage(1); }, [searchTerm, pageSize]);

    const scoped = visibleForScope(rows, p => isClosedProgram(p.status), scope);
    const filtered = scoped.filter(p =>
        !searchTerm ||
        p.program_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.location_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                    />
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Program</th>
                                        <th className="table-list-th">Role</th>
                                        <th className="table-list-th">Geography</th>
                                        <th className="table-list-th">Window</th>
                                        <th className="table-list-th table-list-cell--status table-list-th--right">Status</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && (
                                        <Skeleton.TableRows cols={['2xs', 'md', 'pill', 'md', 'sm', { width: 'pill', align: 'right' }]} />
                                    )}
                                    {!loading && filtered.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="table-list-cell">
                                                <NoDataFound
                                                    title={scope === 'archive' ? 'No Closed Risk Assessments' : 'No Risk Assessment Programs Found'}
                                                    message={scope === 'archive' ? 'No closed risk assessments.' : 'No risk assessment programs are assigned to you.'}
                                                />
                                            </td>
                                        </tr>
                                    )}
                                    {!loading && paginatedRows.map((p, idx) => {
                                        const isChecker = p.checker_id === myId && p.maker_id !== myId;
                                        return (
                                            <tr key={p.id} className="table-list-row"
                                                onClick={() => { setOpenIsChecker(isChecker); setOpenId(p.id); }}>
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title">{p.program_name}</span>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="status-badge xs info">
                                                        {isChecker ? 'Checker' : 'Maker'}
                                                    </span>
                                                </td>
                                                <td className="table-list-cell">{p.location_name || '—'}</td>
                                                <td className="table-list-cell">
                                                    <span className="text-xs text-gray-600">{formatDate(p.start_date)} → {formatDate(p.end_date)}</span>
                                                </td>
                                                <td className="table-list-cell table-list-cell--right">
                                                    <span className={statusBadgeCss(p.status)}>
                                                        {p.status || 'Not-Started'}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="programs"
                        />
                    </div>
                </div>
            </div>

            {openId !== null && (
                <RiskAssessmentExecutionModal
                    programId={openId}
                    isChecker={openIsChecker}
                    readOnly={readOnly}
                    onClose={() => setOpenId(null)}
                    onSaved={refresh}
                />
            )}
        </div>
    );
};

export default WorkRiskAssessmentsBody;
